import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { supabase, Nomination, Category } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useCategories } from '../../hooks/useCategories';
import NominationDetails from '../nominations/NominationDetails';
import Dialog, { DialogVariant } from '../ui/Dialog';
import '../landing/Landing.css';

const HOST = { name: 'Madrid Talento', logo: '/img/sponsors/madrid-talento.png' };
const SPONSORS: { name: string; logo: string; sizeClass: string }[] = [
  { name: 'Grupo GTT', logo: '/img/sponsors/gtt.png', sizeClass: 'landing-sponsor-logo-gtt' },
  { name: 'Tendios', logo: '/img/sponsors/tendios.png', sizeClass: 'landing-sponsor-logo-tendios' },
  { name: 'Nutco', logo: '/img/sponsors/nutco.png', sizeClass: 'landing-sponsor-logo-nutco' },
];

// Mismas proporciones que en la landing (.landing-sponsor-logo-*), escaladas
// ~0.43x para encajar en esta cabecera compacta, sin usar transform: scale
// (que dejaría hueco vacío alrededor al no achicar la caja del elemento).
const SPONSOR_COMPACT_SIZE: Record<string, React.CSSProperties> = {
  'Grupo GTT': { width: 41, height: 32 },
  Tendios: { width: 74, height: 14 },
  Nutco: { width: 48, height: 32 },
};

const PublicVoting: React.FC = () => {
  const { user } = useAuth();
  const { categories } = useCategories();

  const [loading, setLoading] = useState(true);
  const [votingEnabled, setVotingEnabled] = useState<boolean | null>(null);
  const [finalistsByCategory, setFinalistsByCategory] = useState<Record<string, Nomination[]>>({});
  const [myVotes, setMyVotes] = useState<Record<string, string>>({}); // category_id -> nomination_id
  const [selection, setSelection] = useState<Record<string, string>>({}); // pending selection
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [detailsNomination, setDetailsNomination] = useState<Nomination | null>(null);
  const [dialog, setDialog] = useState<{
    open: boolean;
    title?: string;
    message: React.ReactNode;
    variant?: DialogVariant;
    confirmText?: string;
    onConfirm?: () => void;
  }>({ open: false, message: '' });

  useEffect(() => {
    if (user) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, categories.length]);

  const loadAll = async () => {
    try {
      setLoading(true);

      // 1) Config
      const { data: cfg } = await supabase
        .schema('premios')
        .from('platform_config')
        .select('public_voting_enabled')
        .single();

      const enabled = !!cfg?.public_voting_enabled;
      setVotingEnabled(enabled);
      if (!enabled) return;

      if (categories.length === 0) return;

      // 2) Aprobadas
      const { data: noms, error: nomsErr } = await supabase
        .schema('premios')
        .from('nominations')
        .select('*')
        .eq('status', 'approved');
      if (nomsErr) throw nomsErr;

      // 3) Top-3 real por categoría, calculado en el servidor (RPC con
      // SECURITY DEFINER): la RLS de jury_votes no deja leer los votos del
      // jurado desde el navegador de un votante, así que el ranking NO se
      // puede calcular en el cliente.
      const { data: finalistRows, error: finErr } = await supabase
        .schema('premios')
        .rpc('get_public_voting_finalists');
      if (finErr) throw finErr;

      const nomsById = new Map((noms || []).map((n: any) => [n.id, n as Nomination]));
      const byCat: Record<string, Nomination[]> = {};
      (finalistRows as { category_id: string; nomination_id: string; rank: number }[] || [])
        .sort((a, b) => a.rank - b.rank)
        .forEach((row) => {
          const nom = nomsById.get(row.nomination_id);
          if (!nom) return;
          (byCat[row.category_id] = byCat[row.category_id] || []).push(nom);
        });
      setFinalistsByCategory(byCat);

      // 5) Mis votos previos
      if (user) {
        const { data: myV } = await supabase
          .schema('premios')
          .from('public_votes')
          .select('category_id, nomination_id')
          .eq('voter_id', user.id);
        const map: Record<string, string> = {};
        (myV || []).forEach((r: any) => {
          map[r.category_id] = r.nomination_id;
        });
        setMyVotes(map);
      }
    } catch (err: any) {
      console.error('Error loading public voting:', err);
      setDialog({
        open: true,
        title: 'Error',
        message: 'No se ha podido cargar la votación. Inténtalo de nuevo en unos instantes.',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmVote = (category: Category) => {
    const nominationId = selection[category.id];
    if (!nominationId) return;
    const finalists = finalistsByCategory[category.id] || [];
    const chosen = finalists.find((n) => n.id === nominationId);
    const title = chosen?.candidate_name || chosen?.project_title || 'esta candidatura';
    setDialog({
      open: true,
      title: 'Confirmar voto',
      variant: 'info',
      confirmText: 'Sí, votar',
      message: (
        <>
          ¿Confirmas que das tu voto a <strong>{title}</strong> en la categoría{' '}
          <strong>{category.name}</strong>? Una vez confirmado, <strong>no podrás modificarlo</strong>.
        </>
      ),
      onConfirm: () => handleVote(category),
    });
  };

  const handleVote = async (category: Category) => {
    const nominationId = selection[category.id];
    if (!nominationId || !user) return;

    try {
      setSubmitting(category.id);
      const { error } = await supabase
        .schema('premios')
        .from('public_votes')
        .insert([{ category_id: category.id, nomination_id: nominationId, voter_id: user.id }]);
      if (error) throw error;

      setMyVotes((m) => ({ ...m, [category.id]: nominationId }));
      setDialog({ open: true, title: 'Voto registrado', message: '¡Gracias por votar!', variant: 'success' });
    } catch (err: any) {
      console.error('Error voting:', err);
      // 42501: la base de datos rechaza el voto porque esa candidatura no es
      // (o ya no es) una de las 3 finalistas reales de la categoría — es el
      // guardarraíl de premios.is_public_voting_finalist(). Refrescamos la
      // lista para que el votante vea el top-3 correcto y pueda reintentar.
      if (err?.code === '42501') {
        setDialog({
          open: true,
          title: 'Esta candidatura no es finalista',
          message:
            'Esta candidatura ya no aparece entre las 3 finalistas de la categoría. Hemos actualizado la lista, revísala e inténtalo de nuevo.',
          variant: 'error',
        });
        loadAll();
      } else {
        setDialog({
          open: true,
          title: 'Error',
          message: 'No se ha podido registrar tu voto. Inténtalo de nuevo en unos instantes.',
          variant: 'error',
        });
      }
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (votingEnabled === false) {
    return (
      <div
        className="informe-page"
        style={{
          minHeight: 'calc(100vh - 138px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="finaltwo-slide-card" style={{ maxWidth: 560, textAlign: 'center', margin: '40px 20px' }}>
          <span className="finaltwo-hero-kicker" style={{ marginBottom: 18 }}>
            <Sparkles size={14} /> Votación pública cerrada
          </span>
          <h1 className="finaltwo-hero-title" style={{ fontSize: 'clamp(24px, 3.6vw, 34px)' }}>
            La votación pública ha finalizado
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', margin: '14px 0 28px', fontSize: 15 }}>
            Ya puedes ver a los 2 últimos finalistas de cada categoría.
          </p>
          <Link to="/finalistas-2026" className="landing-btn landing-btn-primary">
            <Sparkles size={18} style={{ marginRight: 8 }} />
            Ver los 2 últimos finalistas
          </Link>
        </div>
      </div>
    );
  }

  const categoriesWithFinalists = categories.filter((c) => finalistsByCategory[c.id]?.length);

  return (
    <div className="container dashboard">
      <div style={{ padding: '20px 0', marginBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="landing-host-sponsors-row" style={{ gap: 40 }}>
          <div className="landing-host-sponsors-col">
            <span className="landing-host-label">Organiza</span>
            <img
              src="/premios-logo.svg"
              alt="Fundación NovaGob"
              className="landing-host-logo"
              style={{ maxWidth: 115, maxHeight: 40 }}
            />
          </div>

          <div className="landing-host-sponsors-divider" style={{ minHeight: 40 }} />

          <div className="landing-host-sponsors-col">
            <span className="landing-host-label">Anfitrión</span>
            <img
              src={HOST.logo}
              alt={HOST.name}
              className="landing-host-logo"
              style={{ maxWidth: 115, maxHeight: 40 }}
            />
          </div>

          <div className="landing-host-sponsors-divider" style={{ minHeight: 40 }} />

          <div className="landing-host-sponsors-col">
            <span className="landing-host-label">Patrocinadores</span>
            <div className="landing-sponsors-row" style={{ gap: 32 }}>
              {SPONSORS.map((s) => (
                <img
                  key={s.name}
                  src={s.logo}
                  alt={s.name}
                  className={`landing-sponsor-logo ${s.sizeClass}`}
                  style={SPONSOR_COMPACT_SIZE[s.name]}
                />
              ))}
            </div>
          </div>

          <div className="landing-host-sponsors-divider" style={{ minHeight: 40 }} />

          <div className="landing-host-sponsors-col">
            <span className="landing-host-label">Colaborador</span>
            <img
              src="/img/sponsors/ajuntament-mollet.png"
              alt="Ajuntament de Mollet del Vallès"
              className="landing-host-logo"
              style={{ maxWidth: 115, maxHeight: 40 }}
            />
          </div>
        </div>
      </div>

      <div className="dashboard-header">
        <h1>Votación Pública</h1>
        <p>
          Elige tu candidatura favorita entre las 3 finalistas de cada categoría. Solo puedes votar una vez por
          categoría.
        </p>
      </div>

      {categoriesWithFinalists.length === 0 ? (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="empty-state">
            <h3>No hay finalistas disponibles</h3>
            <p>Todavía no se han determinado finalistas en ninguna categoría.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 24 }}>
          {categoriesWithFinalists.map((category) => {
            const finalists = finalistsByCategory[category.id];
            const myVote = myVotes[category.id];
            const selected = selection[category.id];

            return (
              <div key={category.id} className="card" style={{ padding: 24 }}>
                <h2 style={{ margin: 0, marginBottom: 4 }}>{category.name}</h2>
                <p style={{ margin: 0, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                  {myVote ? 'Ya has votado en esta categoría.' : 'Selecciona una candidatura y confirma tu voto.'}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {finalists.map((nom) => {
                    const isMyVote = myVote === nom.id;
                    const isSelected = selected === nom.id;
                    const disabled = !!myVote;
                    const title = nom.candidate_name || nom.project_title || 'Sin título';

                    return (
                      <label
                        key={nom.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '14px 16px',
                          borderRadius: 10,
                          border: `1px solid ${
                            isMyVote
                              ? 'rgba(16, 185, 129, 0.6)'
                              : isSelected
                              ? '#ffc700'
                              : 'rgba(255,255,255,0.12)'
                          }`,
                          background: isMyVote
                            ? 'rgba(16, 185, 129, 0.1)'
                            : isSelected
                            ? 'rgba(255, 199, 0, 0.08)'
                            : 'rgba(255,255,255,0.03)',
                          cursor: disabled ? 'default' : 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="radio"
                          name={`vote-${category.id}`}
                          value={nom.id}
                          checked={isMyVote || isSelected}
                          disabled={disabled}
                          onChange={() => setSelection((s) => ({ ...s, [category.id]: nom.id }))}
                          style={{ accentColor: '#ffc700' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{title}</div>
                          {nom.institution && (
                            <div
                              style={{
                                fontSize: '0.85rem',
                                color: 'var(--color-text-secondary)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {nom.institution}
                            </div>
                          )}
                        </div>
                        {isMyVote && (
                          <span
                            style={{
                              fontSize: '0.8rem',
                              color: '#86efac',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Tu voto
                          </span>
                        )}
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={(e) => {
                            e.preventDefault();
                            setDetailsNomination(nom);
                          }}
                          style={{ fontSize: 12, padding: '4px 10px' }}
                        >
                          Ver detalles
                        </button>
                      </label>
                    );
                  })}
                </div>

                {!myVote && (
                  <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-primary"
                      disabled={!selected || submitting === category.id}
                      onClick={() => confirmVote(category)}
                    >
                      {submitting === category.id ? 'Enviando...' : 'Confirmar voto'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {detailsNomination && (
        <div className="modal-overlay" onClick={() => setDetailsNomination(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <NominationDetails
              nomination={detailsNomination}
              category={categories.find((c) => c.id === detailsNomination.category_id)}
              onClose={() => setDetailsNomination(null)}
              showActions={false}
              publicView={true}
            />
          </div>
        </div>
      )}

      <Dialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        variant={dialog.variant}
        confirmText={dialog.confirmText}
        onConfirm={dialog.onConfirm}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
      />
    </div>
  );
};

export default PublicVoting;
